// src/hooks/useNewFeatures.js
import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'horizontepyme_seen_features';

/**
 * Hook to track new completed features and show notifications
 * @returns {Object} { newFeaturesCount, markAllAsSeen, hasNewFeatures }
 */
export const useNewFeatures = () => {
    const [newFeaturesCount, setNewFeaturesCount] = useState(0);

    // Define current completed features with unique IDs
    // IMPORTANT: Add new entry here when a feature is completed
    const currentCompletedFeatures = [
        {
            id: 'facturas-reenviar-items-carrito-2024-12',
            name: 'Reenviar items de Factura a Carrito',
            module: 'Facturas',
            dateAdded: '2024-12-01'
        },
        {
            id: 'etiquetas-nuevo-formato-2024-11',
            name: 'Nuevo Formato',
            module: 'Etiquetas',
            dateAdded: '2024-11-15'
        },
        {
            id: 'carrito-producto-personalizado-2024-11',
            name: 'Producto Personalizado',
            module: 'Carrito',
            dateAdded: '2024-11-20'
        }
    ];

    // Get seen features from localStorage
    const getSeenFeatures = useCallback(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            return stored ? JSON.parse(stored) : [];
        } catch (error) {
            console.error('Error reading seen features:', error);
            return [];
        }
    }, []);

    // Save seen features to localStorage
    const saveSeenFeatures = useCallback((featureIds) => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(featureIds));
        } catch (error) {
            console.error('Error saving seen features:', error);
        }
    }, []);

    // Calculate new features count
    useEffect(() => {
        const seenFeatures = getSeenFeatures();
        const currentFeatureIds = currentCompletedFeatures.map(f => f.id);
        
        // Find features that are in current list but not in seen list
        const unseenFeatures = currentFeatureIds.filter(id => !seenFeatures.includes(id));
        
        setNewFeaturesCount(unseenFeatures.length);
    }, [getSeenFeatures]);

    // Listen for storage changes (when features are marked as seen)
    useEffect(() => {
        const handleStorageChange = () => {
            const seenFeatures = getSeenFeatures();
            const currentFeatureIds = currentCompletedFeatures.map(f => f.id);
            const unseenFeatures = currentFeatureIds.filter(id => !seenFeatures.includes(id));
            setNewFeaturesCount(unseenFeatures.length);
        };

        // Custom event for same-window updates
        window.addEventListener('newFeaturesUpdate', handleStorageChange);
        
        return () => {
            window.removeEventListener('newFeaturesUpdate', handleStorageChange);
        };
    }, [getSeenFeatures]);

    // Mark all current features as seen
    const markAllAsSeen = useCallback(() => {
        const allFeatureIds = currentCompletedFeatures.map(f => f.id);
        saveSeenFeatures(allFeatureIds);
        setNewFeaturesCount(0);
        
        // Dispatch custom event to notify other components
        window.dispatchEvent(new Event('newFeaturesUpdate'));
    }, [currentCompletedFeatures, saveSeenFeatures]);

    // Check if there are new features
    const hasNewFeatures = newFeaturesCount > 0;

    return {
        newFeaturesCount,
        hasNewFeatures,
        markAllAsSeen,
        currentCompletedFeatures
    };
};
